import capitalize from 'lodash/capitalize';

import { Badge, Box, Flex, Text, VStack, useColorModeValue } from '@vimeo/bokeh';
import { translate } from '@vimeo/i18n-runtime';
import { type PaywallTrackingProps } from '@vimeo/picox';
import {
  type SubscriptionPlans,
  useGetSubscriptionPlansData,
} from '@vimeo/subscription-api-plans-data-hook';

import { FeatureRow, OtherPlansButton, PaywallCTAsWrapper, PurchaseButton } from '../components';
import { getFeatures } from '../FeatureListPaywall';

interface GenericLimitPaywallProps {
  isUserEligibleForFreeTrial?: boolean;
  tier?: Partial<SubscriptionPlans['tier']>;
  planName?: string;
  paywallTracking: PaywallTrackingProps;
}

export function GenericLimitPaywall({
  tier = 'starter',
  planName,
  paywallTracking,
  isUserEligibleForFreeTrial = false,
}: GenericLimitPaywallProps) {
  const plans = useGetSubscriptionPlansData([tier], undefined, false);
  const planData = plans?.find((plan) => plan.tier === tier) ?? plans?.[0];
  const storageQuota = planData?.metadata?.entitlements?.params?.videoStoragePeriodicQuota;
  const hasTrialPath = Boolean(planData?.metadata?.interactions?.purchase?.uri?.freeTrial);
  const isTrial = hasTrialPath && isUserEligibleForFreeTrial;

  const joinVimeoTitle = translate('Join Vimeo');
  const isLongTitle = joinVimeoTitle.length > 12;
  const badgeText = planName ?? (tier ? capitalize(`${tier}`) : '');
  const headerImageUrl = useColorModeValue(
    'https://i.vimeocdn.com/custom_asset/4a6f0f615c4cf9d30b48f79df970438e',
    'https://i.vimeocdn.com/custom_asset/edb2ec4f5620a90adb065894bbb08419',
  );

  return (
    <Flex
      direction="column"
      alignItems="center"
      width="100%"
      height={{ base: '100%', md: 'auto' }}
      maxHeight={{ base: 'none', md: 'calc(100vh - 120px)' }}
      position="relative"
    >
      <Flex
        direction="column"
        alignItems="center"
        width="100%"
        maxWidth="400px"
        gap="24px"
        flex="1 1 auto"
        pt={{ base: '24px', md: '0' }}
        pb="24px"
        mx={{ base: 'auto', md: '32px' }}
        overflowY="auto"
      >
        <Box
          width="100%"
          maxWidth="252px"
          maxHeight="200px"
          minHeight="120px"
          aspectRatio="1333/1058"
        >
          <img
            src={headerImageUrl}
            alt=""
            style={{
              width: '100%',
              height: '100%',
              objectFit: 'contain',
            }}
          />
        </Box>

        <Flex direction="column" alignItems="center" gap="16px" width="100%" paddingX="32px">
          <Flex
            direction={{ base: 'column', md: 'row' }}
            alignItems="center"
            gap={{ base: 'xs', md: '8px' }}
            justifyContent="center"
          >
            <Text
              variant={{ base: 'heading-lg', md: isLongTitle ? 'heading-xl' : 'heading-2xl' }}
              whiteSpace="nowrap"
            >
              {joinVimeoTitle}
            </Text>
            <Box pt={{ base: '0', md: '8px' }}>
              <Badge variant="upgrade" px="6px" fontSize="heading-xs">
                {badgeText}
              </Badge>
            </Box>
          </Flex>
          <Text variant="heading-sm" fontWeight="medium" color="text-primary" textAlign="center">
            {translate('All the tools to create, manage, and showcase video.')}
          </Text>
        </Flex>

        <VStack gap="md" alignItems="start" width="100%" pt="16px">
          {getFeatures(storageQuota).map((props, idx) => (
            <FeatureRow key={`row${idx}`} {...props} />
          ))}
        </VStack>
      </Flex>

      <PaywallCTAsWrapper>
        <PurchaseButton
          subscriptionTier={tier}
          isTrial={isTrial}
          planName={badgeText}
          paywallTracking={paywallTracking}
        />
        <OtherPlansButton paywallTracking={paywallTracking} />
      </PaywallCTAsWrapper>
    </Flex>
  );
}
